import React, { useEffect, useState, useRef } from "react";
import { Table, Tag, message, DatePicker } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Instance from "../../AxiosConfig";
import { Form } from "react-bootstrap";

const TDSTable = () => {
  const navigate = useNavigate();
  const [invoiceData, setInvoiceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const hiddenContainerRef = useRef(null);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [currency, setCurrency] = useState("All");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [totalTDS, setTotalTDS] = useState(0);

  const debounceTimer = useRef(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const handleClearFilters = () => {
    setSearch("");
    setStatus("All");
    setCurrency("All");
    setFromDate(null);
    setToDate(null);
  };

  const fetchFilteredData = async (
    page = pagination.current,
    limit = pagination.pageSize,
  ) => {
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (search.trim()) params.append("search", search.trim());
      if (status !== "All") params.append("status", status);
      if (currency !== "All") params.append("currency", currency);

      if (fromDate) params.append("startDate", fromDate);
      if (toDate) params.append("endDate", toDate);

      // 🔍 Log Query Params
      console.log("🟦 Query Params:", Object.fromEntries(params));

      const queryString = params.toString();
      const endpoint = queryString
        ? `/invoices/filter/search?${queryString}`
        : `/invoices`;

      console.log("📡 Final API URL:", endpoint);

      const res = await Instance.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("📥 API Raw Response:", res?.data);

      // const invoices = res?.data?.invoices || [];const data = response?.data || {};
      const data = res?.data || {};
const invoices = data.invoices || [];

setInvoiceData(invoices);

// 👇 THIS IS THE MAIN FIX
if (data.total) {
  setPagination(prev => ({
    ...prev,
    total: data.total,
    current: data.page || page,
  }));
} else {
  // normal API (no pagination)
  setPagination(prev => ({
    ...prev,
    total: invoices.length,
    current: 1,
  }));
}

      console.log("📘 Total Invoices Received:", invoices.length);

      // REQUIRED FILTER: INR + TDS > 0
      const filtered = invoices.filter(
        (inv) => inv.currency === "INR" && Number(inv.TotalTDSAmount) > 0,
      );

      console.log("📌 Filtered Invoices (INR + TDS>0):", filtered);

      setInvoiceData(filtered);
    } catch (error) {
      console.error("❌ Invoice Fetch Error:", error);
      message.error("Error fetching invoice data");
    } finally {
      setLoading(false);
      console.log("🟠 fetchFilteredData() FINISHED");
    }
  };

  // -------------------------------------------------------
  // FETCH TDS TOTAL SUMMARY
  // -------------------------------------------------------
  const fetchTDSReport = async () => {
    try {
      const token = localStorage.getItem("token");

      const params = new URLSearchParams();

      if (fromDate) params.append("startDate", fromDate);
      if (toDate) params.append("endDate", toDate);

      const url =
        params.toString().length > 0
          ? `/TDSReport/tds-report?${params.toString()}`
          : `/TDSReport/tds-report`;

      const res = await Instance.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const total = Number(res?.data?.totalTDSAmount || 0);
      setTotalTDS(total);
    } catch (err) {
      console.error("TDS Summary Error:", err);
    }
  };

  // -------------------------------------------------------
  // DEBOUNCED FETCH FOR FILTERS
  // -------------------------------------------------------
  const debouncedFetch = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      fetchFilteredData();
      fetchTDSReport(); // <-- TDS summary also updates with same filter
    }, 400);
  };

  // -------------------------------------------------------
  // INITIAL LOAD
  // -------------------------------------------------------
  useEffect(() => {
    fetchFilteredData();
    fetchTDSReport();
  }, []);

  // -------------------------------------------------------
  // RUN WHEN FILTERS CHANGE
  // -------------------------------------------------------
  useEffect(() => {
    debouncedFetch();

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [search, status, currency, fromDate, toDate]);

  // -------------------------------------------------------
  // TABLE COLUMNS
  // -------------------------------------------------------
  const columns = [
    {
      title: "Invoice No",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      fixed: "left",
      render: (val) => <strong>{val}</strong>,
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: "Agent",
      key: "agentName",
      render: (_, record) => record?.agentId?.agentName || "—",
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      render: (currency) => (
        <Tag
          color={currency === "INR" ? "green" : "gold"}
          style={{ fontWeight: 500 }}
        >
          {currency}
        </Tag>
      ),
    },
    {
      title: "Total Closure",
      dataIndex: "baseClosureAmount",
      key: "baseClosureAmount",
      render: (val, record) =>
        `${record.currency} ${(val || 0).toLocaleString()}`,
    },
    {
      title: "Term Amount",
      render: (_, record) =>
        `${record.currency} ${record.terms[0]?.baseAmount}`,
    },
    {
      title: "TDS Amount",
      render: (record) => `${record.currency} ${record.TotalTDSAmount}`,
    },
    {
      title: "Money Received",
      dataIndex: "moneyReceived",
      key: "moneyReceived",
      render: (val) => (val ? `INR ${val.toLocaleString()}` : "—"),
    },
    {
      title: "Pending Payment (INR)",
      dataIndex: "PendingPaymentInINR",
      key: "PendingPaymentInINR",
      render: (val, record) => (
        <span style={{ color: "#ff4d4f", fontWeight: 600 }}>
          {record.currency} {Number(val || 0).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Created On",
      dataIndex: "InvoiceDate",
      key: "createdAt",
      render: (val) => dayjs(val).format("DD MMM YYYY"),
    },
  ];

  return (
    <>
      <div className="filter-card p-3">
        <Form>
          <div className="row g-3">
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <Form.Control
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by company or invoice no..."
                />
              </Form.Group>
            </div>

            <div className="col-md-2">
              <Form.Group>
                <Form.Label>From Date</Form.Label>
                <DatePicker
                  className="form-control"
                  value={fromDate ? dayjs(fromDate) : null}
                  onChange={(d) =>
                    setFromDate(d ? d.format("YYYY-MM-DD") : null)
                  }
                  format="YYYY-MM-DD"
                />
              </Form.Group>
            </div>

            <div className="col-md-2">
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <DatePicker
                  className="form-control"
                  value={toDate ? dayjs(toDate) : null}
                  onChange={(d) => setToDate(d ? d.format("YYYY-MM-DD") : null)}
                  format="YYYY-MM-DD"
                />
              </Form.Group>
            </div>

            <div className="col-md-1 d-flex align-items-end">
              <button
                type="button"
                className="clear-btn-company w-100"
                onClick={handleClearFilters}
                style={{ height: "38px" }}
              >
                Clear
              </button>
            </div>
          </div>
        </Form>

        {(search ||
          status !== "All" ||
          currency !== "All" ||
          fromDate ||
          toDate) && (
          <div className="mt-3">
            <small className="text-muted">
              {loading
                ? "Loading filtered results..."
                : `Showing ${invoiceData.length} result(s)`}
            </small>
          </div>
        )}
      </div>

      {/* ⭐ TOTAL TDS SUMMARY */}
      <div className="d-flex justify-content-start my-3">
        <h4 style={{ fontWeight: "700" }}>
          Total TDS Amount: INR {totalTDS.toLocaleString()}
        </h4>
      </div>

      <div className="taxinvoice-wrapper mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 style={{ fontWeight: "600" }}>TDS Reports</h2>
        </div>

        <Table
          columns={columns}
          dataSource={invoiceData}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
          }}
          onChange={(pag) => {
            setPagination(pag);
            fetchFilteredData(pag.current, pag.pageSize);
          }}
          scroll={{ x: "max-content" }}
          bordered
          rowKey={(record) => record._id}
        />
      </div>
    </>
  );
};

export default TDSTable;
